import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight } from "lucide-react";
import { CrmContactChannelsDisplay } from "@/components/crm/CrmContactChannelsDisplay";
import { apiGet } from "@/lib/apiClient";
import {
  addressOptionsFromLeadFields,
} from "@/lib/crmContactAddresses";
import {
  channelOptionsFromLeadFields,
} from "@/lib/crmContactChannels";
import {
  formatCrmLeadTypeLabel,
  formatCrmSourceLabel,
  formatCrmStatusLabel,
} from "@/lib/crmDisplay";
import {
  buildLeadDetailConnectedRecords,
  leadDetailConnectedSelectedRecordId,
} from "@/lib/crmLeadDetailConnectedRecords";
import {
  crmLeadsListBackHref,
  resolveCrmLeadListNeighbors,
  type CrmLeadListNavEntry,
} from "@/lib/crmLeadListNav";
import {
  buildLeadCompanyDetailView,
  buildLeadContactChannelsView,
  leadContactDisplayName,
  leadContactSummaryLine,
  type LeadDetailContactSnapshot,
  type LeadDetailFetchedContact,
  type LeadLinkedAccountSnapshot,
} from "@/lib/crmLeadDetailDisplay";
import { fetchCrmLeadModuleFields, type CrmLeadModuleFieldsDocument } from "@/lib/crmLeadModuleFieldsApi";
import {
  crmOrgUserDisplayName,
  useCrmOrgUserDirectory,
  type CrmOrgUserRow,
} from "@/lib/crmOwnerUsers";
import {
  crmLeadSelectOptionLabel,
  crmLeadSelectOptionsForParentForm,
  crmLeadSectionFieldById,
  standardIndustrySegmentSelectOptions,
  standardIndustrySelectOptions,
} from "@/lib/crmLeadModuleFields";
import { CrmActivityPanel, type ActivityTouchpoint } from "@/components/crm/CrmActivityPanel";
import { ConnectedRecordsPanel } from "@/components/crm/ConnectedRecordsPanel";
import { CrmRecordDetailLayout } from "@/components/crm/CrmRecordDetailLayout";
import { resolveCrmRecordTab } from "@/components/crm/CrmRecordTabs";
import { RecordTimeline, type RecordTimelineEvent } from "@/components/crm/RecordTimeline";

type LeadContact = LeadDetailContactSnapshot;

type LeadRecord = {
  leadName: string;
  stage: string;
  score: number;
  status: string;
  ownerId: string | null;
  source: string;
  companyName: string | null;
  companyDomain: string | null;
  companyWebsite: string | null;
  companyIndustry: string | null;
  companyIndustrySegment?: string | null;
  id: string;
  leadType?: string;
  accountId?: string | null;
  primaryContactId?: string | null;
  sourceDetail?: string | null;
  contacts: LeadContact[];
};

type DealRow = { id: string; name?: string | null; stage?: string | null; accountId?: string };
type LeadRow = { id: string; leadName?: string; stage?: string; status?: string; accountId?: string | null };

function formatWebsiteHref(website: string | null | undefined): string | null {
  const raw = website?.trim();
  if (!raw) return null;
  return /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
}

function leadDetailNeighborHref(
  entry: CrmLeadListNavEntry,
  tabSearch: URLSearchParams,
  activeTab: ReturnType<typeof resolveCrmRecordTab>,
): string {
  const params = new URLSearchParams(tabSearch);
  if (entry.contact?.trim()) params.set("contact", entry.contact.trim());
  else params.delete("contact");
  if (activeTab !== "overview") params.set("tab", activeTab);
  else params.delete("tab");
  const qs = params.toString();
  const base = `/crm/leads/${encodeURIComponent(entry.leadId)}`;
  return qs ? `${base}?${qs}` : base;
}

export function CrmLeadDetailPage() {
  const { leadId: idParam } = useParams<{ leadId: string }>();
  const leadId = idParam ? decodeURIComponent(idParam) : "";
  const [searchParams] = useSearchParams();
  const activeTab = resolveCrmRecordTab(searchParams.get("tab"));
  const selectedContactParam = searchParams.get("contact") || "0";
  const focusParam = searchParams.get("focus");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lead, setLead] = useState<LeadRecord | null>(null);
  const [linkedAccount, setLinkedAccount] = useState<LeadLinkedAccountSnapshot | null>(null);
  const [fetchedContacts, setFetchedContacts] = useState<Record<string, LeadDetailFetchedContact>>({});
  const [deals, setDeals] = useState<DealRow[]>([]);
  const [relatedLeads, setRelatedLeads] = useState<LeadRow[]>([]);
  const [touchpoints, setTouchpoints] = useState<ActivityTouchpoint[]>([]);
  const [timeline, setTimeline] = useState<RecordTimelineEvent[]>([]);
  const [leadFieldSections, setLeadFieldSections] = useState<
    CrmLeadModuleFieldsDocument["sections"] | null
  >(null);
  const { lookup: orgUserLookup, resolveOwnerLabel } = useCrmOrgUserDirectory();
  const [ownerProfile, setOwnerProfile] = useState<CrmOrgUserRow | null>(null);
  const tabSearch = useMemo(() => {
    const p = new URLSearchParams(searchParams);
    p.delete("tab");
    return p;
  }, [searchParams]);

  useEffect(() => {
    let cancelled = false;
    void fetchCrmLeadModuleFields().then((doc) => {
      if (!cancelled) setLeadFieldSections(doc.sections);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!leadId) return;
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setLinkedAccount(null);
      setFetchedContacts({});
      const res = await apiGet<LeadRecord>(`/api/crm/leads/${encodeURIComponent(leadId)}`);
      if (cancelled) return;
      if (!res.ok || !res.data) {
        setError(res.error || "Lead not found.");
        setLead(null);
        setLoading(false);
        return;
      }
      setLead(res.data);
      setError(null);

      const accountId = res.data.accountId;
      const contactIds = (res.data.contacts || [])
        .map((c) => c.contactId)
        .filter(Boolean) as string[];

      const [dealsRes, leadsRes, tpRes, accountRes, ...contactResults] = await Promise.all([
        accountId
          ? apiGet<{ deals?: DealRow[] }>(`/api/crm/deals?accountId=${encodeURIComponent(accountId)}`)
          : Promise.resolve(null),
        apiGet<{ leads?: LeadRow[] }>("/api/crm/leads?limit=200"),
        accountId
          ? apiGet<{ touchpoints?: ActivityTouchpoint[] }>(
              `/api/crm/touchpoints?accountId=${encodeURIComponent(accountId)}&limit=50`,
            )
          : Promise.resolve(null),
        accountId
          ? apiGet<LeadLinkedAccountSnapshot>(`/api/crm/accounts/${encodeURIComponent(accountId)}`)
          : Promise.resolve(null),
        ...contactIds.map((contactId) =>
          accountId
            ? apiGet<LeadDetailFetchedContact>(
                `/api/crm/contacts/${encodeURIComponent(contactId)}?accountId=${encodeURIComponent(accountId)}`,
              )
            : Promise.resolve(null),
        ),
      ]);

      if (cancelled) return;

      setDeals(dealsRes?.ok ? (dealsRes.data?.deals ?? []) : []);
      const allLeads = leadsRes.ok ? (leadsRes.data?.leads ?? []) : [];
      setRelatedLeads(
        accountId ? allLeads.filter((l) => l.accountId === accountId && l.id !== res.data!.id) : [],
      );
      setTouchpoints(tpRes?.ok ? (tpRes.data?.touchpoints ?? []) : []);
      setLinkedAccount(accountRes?.ok && accountRes.data ? accountRes.data : null);

      const byId: Record<string, LeadDetailFetchedContact> = {};
      contactIds.forEach((id, index) => {
        const contactRes = contactResults[index];
        if (contactRes?.ok && contactRes.data) {
          byId[id] = { ...contactRes.data, id: contactRes.data.id || id };
        }
      });
      setFetchedContacts(byId);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  useEffect(() => {
    if (!leadId || activeTab !== "history") {
      setTimeline([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      const res = await apiGet<{ events?: RecordTimelineEvent[] }>(
        `/api/audit/app/crm/module/leads/record/${encodeURIComponent(leadId)}?limit=120`,
      );
      if (cancelled) return;
      setTimeline(res.ok ? (res.data?.events ?? []) : []);
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, activeTab]);

  useEffect(() => {
    if (!lead || activeTab !== "overview") return;
    if ((focusParam || "").trim().toLowerCase() !== "account") return;
    const target = document.getElementById("lead-account-section");
    target?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [lead, activeTab, focusParam]);

  const ownerDisplayName = useMemo(() => {
    if (ownerProfile) return crmOrgUserDisplayName(ownerProfile);
    return resolveOwnerLabel(lead?.ownerId);
  }, [lead?.ownerId, ownerProfile, resolveOwnerLabel]);

  useEffect(() => {
    const ownerId = lead?.ownerId?.trim();
    if (!ownerId) {
      setOwnerProfile(null);
      return;
    }
    if (orgUserLookup[ownerId.toLowerCase()]) {
      setOwnerProfile(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      const res = await apiGet<CrmOrgUserRow>(`/api/users/${encodeURIComponent(ownerId)}`);
      if (cancelled) return;
      setOwnerProfile(res.ok && res.data ? { ...res.data, id: res.data.id || ownerId } : null);
    })();
    return () => {
      cancelled = true;
    };
  }, [lead?.ownerId, orgUserLookup]);

  const accountChannelOptions = useMemo(
    () => channelOptionsFromLeadFields(leadFieldSections ?? undefined, "account_lead", "account"),
    [leadFieldSections],
  );
  const contactChannelOptions = useMemo(
    () => channelOptionsFromLeadFields(leadFieldSections ?? undefined, "contact_lead", "contact"),
    [leadFieldSections],
  );
  const accountAddressTypeOptions = useMemo(
    () => addressOptionsFromLeadFields(leadFieldSections ?? undefined, "account_lead"),
    [leadFieldSections],
  );
  const contactAddressTypeOptions = useMemo(
    () => addressOptionsFromLeadFields(leadFieldSections ?? undefined, "contact_lead"),
    [leadFieldSections],
  );

  const industryField = useMemo(
    () => crmLeadSectionFieldById(leadFieldSections, "account_lead", "industry"),
    [leadFieldSections],
  );
  const industrySegmentField = useMemo(
    () => crmLeadSectionFieldById(leadFieldSections, "account_lead", "industry_segment"),
    [leadFieldSections],
  );

  const companyView = useMemo(() => {
    if (!lead) return null;
    return buildLeadCompanyDetailView({ lead, account: linkedAccount });
  }, [lead, linkedAccount]);

  const industryLabel = useMemo(() => {
    if (!companyView?.industry) return "";
    const options = industryField?.selectOptions?.length
      ? industryField.selectOptions
      : standardIndustrySelectOptions();
    return crmLeadSelectOptionLabel(options, companyView.industry) || companyView.industry;
  }, [companyView?.industry, industryField]);

  const industrySegmentLabel = useMemo(() => {
    if (!companyView?.industrySegment) return "";
    const segmentOptions = industrySegmentField?.selectOptions?.length
      ? crmLeadSelectOptionsForParentForm(
          industrySegmentField.selectOptions,
          companyView.industry,
          companyView.industrySegment,
        )
      : crmLeadSelectOptionsForParentForm(
          standardIndustrySegmentSelectOptions(),
          companyView.industry,
          companyView.industrySegment,
        );
    return (
      crmLeadSelectOptionLabel(segmentOptions, companyView.industrySegment) ||
      companyView.industrySegment
    );
  }, [companyView?.industry, companyView?.industrySegment, industrySegmentField]);

  const selectedIndexById = lead?.contacts.findIndex(
    (c) => (c.contactId || "").toLowerCase() === selectedContactParam.toLowerCase(),
  );
  const parsedIndex = Number(selectedContactParam);
  const selectedContactIndex =
    selectedIndexById !== undefined && selectedIndexById >= 0
      ? selectedIndexById
      : Number.isFinite(parsedIndex)
        ? Math.min(Math.max(parsedIndex, 0), Math.max(0, (lead?.contacts.length ?? 1) - 1))
        : 0;
  const selectedSnapshot = lead?.contacts[selectedContactIndex] ?? null;
  const selectedContactChannels = useMemo(() => {
    if (!lead || !selectedSnapshot) return null;
    const fetched =
      selectedSnapshot.contactId && fetchedContacts[selectedSnapshot.contactId]
        ? fetchedContacts[selectedSnapshot.contactId]
        : null;
    return buildLeadContactChannelsView({
      snapshot: selectedSnapshot,
      index: selectedContactIndex,
      fetched,
      sourceDetail: lead.sourceDetail,
      leadType: lead.leadType,
    });
  }, [lead, selectedSnapshot, selectedContactIndex, fetchedContacts]);

  const leadContactIds = (lead?.contacts || []).map((c) => c.contactId).filter(Boolean) as string[];

  const listNav = useMemo(() => {
    if (!leadId) {
      return { hasList: false, prev: null, next: null, returnQuery: "" };
    }
    const activeContact =
      selectedContactParam && selectedContactParam !== "0" ? selectedContactParam : null;
    return resolveCrmLeadListNeighbors(leadId, activeContact);
  }, [leadId, selectedContactParam]);

  if (loading) {
    return <p className="text-sm text-slate-500">Loading lead…</p>;
  }
  if (error && !lead) {
    return (
      <div className="space-y-3">
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">{error}</div>
        <Link to="/crm/leads" className="text-sm font-medium text-indigo-600 hover:underline">
          Back to leads
        </Link>
      </div>
    );
  }
  if (!lead) return null;

  const backToLeadsHref = crmLeadsListBackHref(listNav.returnQuery);
  const pageTitle =
    companyView?.name?.trim() || lead.leadName?.trim() || lead.companyName?.trim() || "Lead";

  const basePath = `/crm/leads/${encodeURIComponent(lead.id)}`;
  const connected = buildLeadDetailConnectedRecords({
    lead,
    linkedAccountStatus: linkedAccount?.status ?? null,
    linkedAccountName: linkedAccount?.name ?? null,
    companyDisplayName: companyView?.name ?? null,
    deals,
    relatedLeads,
  });
  const connectedSelectedId = leadDetailConnectedSelectedRecordId({
    leadId: lead.id,
    accountId: lead.accountId,
    status: lead.status,
    linkedAccountStatus: linkedAccount?.status ?? null,
    focusParam,
    contactParam: selectedContactParam,
  });
  const websiteHref = formatWebsiteHref(companyView?.website || lead.companyWebsite);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <Link
            to={backToLeadsHref}
            className="mt-0.5 inline-flex shrink-0 rounded-md border border-gray-300 p-2 text-gray-700 hover:bg-gray-50 dark:border-white/20 dark:text-gray-200 dark:hover:bg-white/10"
            aria-label="Back to leads"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden />
          </Link>
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Lead detail
            </p>
            <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">{pageTitle}</h1>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to={`${basePath}/edit${searchParams.toString() ? `?${searchParams.toString()}` : ""}`}
            className="rounded-md border border-indigo-300 bg-indigo-50 px-3 py-2 text-sm font-medium text-indigo-800 hover:bg-indigo-100 dark:border-indigo-400/50 dark:bg-indigo-900/30 dark:text-indigo-100"
          >
            Edit
          </Link>
          {lead.status !== "converted" ? (
            <Link
              to={`/crm/leads/${encodeURIComponent(lead.id)}/convert`}
              className="rounded-md border border-indigo-300 px-3 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-50 dark:border-indigo-400/50 dark:text-indigo-200 dark:hover:bg-indigo-900/30"
            >
              Convert
            </Link>
          ) : null}
          {listNav.hasList && listNav.prev ? (
            <Link
              to={leadDetailNeighborHref(listNav.prev, tabSearch, activeTab)}
              className="inline-flex rounded-md border border-gray-300 p-2 text-gray-700 hover:bg-gray-50 dark:border-white/20 dark:text-gray-200 dark:hover:bg-white/10"
              aria-label="Previous lead"
            >
              <ChevronLeft className="h-4 w-4" aria-hidden />
            </Link>
          ) : null}
          {listNav.hasList && listNav.next ? (
            <Link
              to={leadDetailNeighborHref(listNav.next, tabSearch, activeTab)}
              className="inline-flex rounded-md border border-gray-300 p-2 text-gray-700 hover:bg-gray-50 dark:border-white/20 dark:text-gray-200 dark:hover:bg-white/10"
              aria-label="Next lead"
            >
              <ChevronRight className="h-4 w-4" aria-hidden />
            </Link>
          ) : null}
        </div>
      </div>

      <CrmRecordDetailLayout
        basePath={basePath}
        activeTab={activeTab}
        searchParams={tabSearch}
        connectedRecords={
          <ConnectedRecordsPanel
            compact
            selectedRecordId={connectedSelectedId}
            accounts={connected.accounts}
            contacts={connected.contacts}
            accountsTitle={connected.accountsTitle}
            contactsTitle={connected.contactsTitle}
            deals={connected.deals}
            leads={connected.leads}
          />
        }
      >
        {activeTab === "overview" ? (
          <div className="space-y-4">
            <section className="rounded-lg border border-gray-200 bg-white p-4 dark:border-white/10 dark:bg-white/5">
              <h2 className="text-lg font-semibold">Lead information</h2>
              <dl className="mt-3 grid gap-3 text-sm md:grid-cols-2">
                <div>
                  <dt className="text-gray-500">Type</dt>
                  <dd className="font-medium">{formatCrmLeadTypeLabel(lead.leadType)}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Stage</dt>
                  <dd className="font-medium">{formatCrmStatusLabel(lead.stage) || "Not set"}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Status</dt>
                  <dd className="font-medium">{formatCrmStatusLabel(lead.status) || "Not set"}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Source</dt>
                  <dd className="font-medium">{formatCrmSourceLabel(lead.source) || "Manual"}</dd>
                </div>
              </dl>
            </section>

            <section
              id="lead-account-section"
              className="rounded-lg border border-gray-200 bg-white p-4 dark:border-white/10 dark:bg-white/5"
            >
              <h2 className="text-lg font-semibold">Lead Account</h2>
              {companyView ? (
                <div className="mt-3 space-y-4">
                  <dl className="grid gap-3 text-sm md:grid-cols-2">
                    <div>
                      <dt className="text-gray-500">Company name</dt>
                      <dd className="font-medium">{companyView.name || "No company name"}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Domain</dt>
                      <dd className="font-medium">{companyView.domain || "No domain"}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Website</dt>
                      <dd className="font-medium">
                        {websiteHref ? (
                          <a href={websiteHref} target="_blank" rel="noreferrer" className="text-indigo-700 hover:underline dark:text-indigo-300">
                            {companyView.website}
                          </a>
                        ) : (
                          "No website"
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Industry</dt>
                      <dd className="font-medium">{industryLabel || "Not set"}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Industry segment</dt>
                      <dd className="font-medium">{industrySegmentLabel || "Not set"}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Owner</dt>
                      <dd className="font-medium">{ownerDisplayName}</dd>
                    </div>
                  </dl>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">Channels</h3>
                    <div className="mt-2">
                      <CrmContactChannelsDisplay
                        channels={companyView.channels}
                        emailContext="account"
                        typeOptions={accountChannelOptions}
                        addressTypeOptions={accountAddressTypeOptions}
                        emptyMessage="No account channels on file."
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <p className="mt-2 text-sm text-gray-500">No Lead Account on this record.</p>
              )}
            </section>

            {lead.accountId ? (
              <CrmActivityPanel
                accountId={lead.accountId}
                initialTouchpoints={touchpoints}
                defaultContactId={lead.primaryContactId ?? leadContactIds[0] ?? null}
                filterToContact={Boolean(lead.primaryContactId || leadContactIds[0])}
                contacts={lead.contacts
                  .filter((c) => c.contactId)
                  .map((c) => ({
                    id: c.contactId as string,
                    label: leadContactDisplayName(c),
                  }))}
                title="Lead activity"
              />
            ) : null}

            <section className="rounded-lg border border-gray-200 bg-white p-4 dark:border-white/10 dark:bg-white/5">
              <h2 className="text-lg font-semibold">Lead Contacts</h2>
              {lead.contacts.length === 0 ? (
                <p className="mt-2 text-sm text-gray-500">No Lead Contacts on this record.</p>
              ) : (
                <div className="mt-3 grid gap-4 lg:grid-cols-[260px,1fr]">
                  <div className="space-y-2">
                    {lead.contacts.map((contact, index) => {
                      const isActive = selectedContactIndex === index;
                      const name = leadContactDisplayName(contact);
                      const channels = buildLeadContactChannelsView({
                        snapshot: contact,
                        index,
                        fetched:
                          contact.contactId && fetchedContacts[contact.contactId]
                            ? fetchedContacts[contact.contactId]
                            : null,
                        sourceDetail: lead.sourceDetail,
                        leadType: lead.leadType,
                      });
                      const contactKey = contact.contactId || String(index);
                      return (
                        <Link
                          key={`${lead.id}-contact-${index}`}
                          to={`${basePath}?contact=${encodeURIComponent(contactKey)}`}
                          className={`block rounded-md border px-3 py-2 text-sm transition ${
                            isActive
                              ? "border-indigo-400 bg-indigo-50 text-indigo-900 dark:border-indigo-400/60 dark:bg-indigo-900/30"
                              : "border-gray-200 hover:bg-gray-50 dark:border-white/10 dark:hover:bg-white/10"
                          }`}
                        >
                          <p className="font-medium">{name}</p>
                          <p className="text-xs text-gray-500">{leadContactSummaryLine(channels)}</p>
                          {contact.title?.trim() ? (
                            <p className="text-xs text-gray-500">{contact.title.trim()}</p>
                          ) : null}
                        </Link>
                      );
                    })}
                  </div>
                  {selectedSnapshot && selectedContactChannels ? (
                    <div className="rounded-md border border-gray-200 p-4 dark:border-white/10">
                      <h3 className="text-sm font-semibold">{leadContactDisplayName(selectedSnapshot)}</h3>
                      <dl className="mt-3 grid gap-3 text-sm md:grid-cols-2">
                        <div>
                          <dt className="text-gray-500">Title</dt>
                          <dd className="font-medium">{selectedSnapshot.title?.trim() || "No title"}</dd>
                        </div>
                        <div>
                          <dt className="text-gray-500">CRM contact ID</dt>
                          <dd className="font-medium">{selectedSnapshot.contactId || "Not linked yet"}</dd>
                        </div>
                      </dl>
                      <div className="mt-4">
                        <CrmContactChannelsDisplay
                          channels={selectedContactChannels}
                          emailContext="contact"
                          typeOptions={contactChannelOptions}
                          addressTypeOptions={contactAddressTypeOptions}
                          emptyMessage="No contact channels on file."
                        />
                      </div>
                    </div>
                  ) : null}
                </div>
              )}
            </section>
          </div>
        ) : (
          <section className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-lg font-semibold">History</h2>
              <Link
                to={`/crm/audit/leads/${encodeURIComponent(lead.id)}`}
                className="text-sm text-indigo-600 hover:underline dark:text-indigo-300"
              >
                Open full audit page
              </Link>
            </div>
            <RecordTimeline events={timeline} emptyMessage="No record history for this lead yet." />
          </section>
        )}
      </CrmRecordDetailLayout>
    </div>
  );
}
